import React from 'react'
import styled from 'styled-components'

// Components
import Measurement from './Measurement'
import Pill from './Pill'
import Line from './Line'

// Utils
import {
  rectMinX,
  rectMinY,
  rectMaxX,
  rectMaxY,
  rectContainsRect,
  rectContainsRectOnAxis,
  rectIntersectsRectOnAxis,
} from 'utils/geometry'

// Types
import { Rect, Side } from 'modules/shares/types'

/**
 * STYLES
 */
const Container = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  z-index: 1; /* Needed to appear above symbol name*/
`

/**
 * UTILS
 */
const measurementToDrawLine = (measurement: Measurement) => {
  if (rectContainsRect(measurement.endRect, measurement.startRect)) {
    return measurement
  }

  if (
    rectIntersectsRectOnAxis(
      measurement.startRect,
      measurement.endRect,
      measurement.axis
    )
  ) {
    return new Measurement({
      startRect: measurement.endRect,
      endRect: measurement.startRect,
      edge: measurement.edge,
    })
  }

  return measurement.reversed()
}

/**
 * HELPER COMPONENTS
 */
const MeasurementView: React.FC<{
  measurement: Measurement
  side: Side
  zoomFactor: number
}> = ({ measurement, zoomFactor, side }) => {
  const drawingMeasurement = measurementToDrawLine(measurement).scaled(
    zoomFactor
  )
  return (
    <>
      <Pill side={side} measurement={drawingMeasurement}>
        {measurement.length.toLocaleString()}
      </Pill>
      <Line side={side} measurement={drawingMeasurement} />
    </>
  )
}

/**
 * MAIN COMPONENT
 */
const Measurements: React.FC<{
  selectedFrame: Rect
  hoveredFrame: Rect
  zoomFactor: number
}> = ({ selectedFrame, hoveredFrame, zoomFactor, ...rest }) => {
  let topMeasurement: Measurement | undefined = undefined
  let rightMeasurement: Measurement | undefined = undefined
  let bottomMeasurement: Measurement | undefined = undefined
  let leftMeasurement: Measurement | undefined = undefined

  if (rectContainsRectOnAxis(hoveredFrame, selectedFrame, 'x')) {
    leftMeasurement = new Measurement({
      startRect: selectedFrame,
      endRect: hoveredFrame,
      edge: 'minX',
    })
    rightMeasurement = new Measurement({
      startRect: selectedFrame,
      endRect: hoveredFrame,
      edge: 'maxX',
    })
  } else {
    if (rectMaxX(selectedFrame) > rectMaxX(hoveredFrame)) {
      leftMeasurement = new Measurement({
        startRect: selectedFrame,
        endRect: hoveredFrame,
        edge: 'minX',
      })
    }

    if (rectMinX(selectedFrame) < rectMinX(hoveredFrame)) {
      rightMeasurement = new Measurement({
        startRect: selectedFrame,
        endRect: hoveredFrame,
        edge: 'maxX',
      })
    }
  }
  if (rectContainsRectOnAxis(hoveredFrame, selectedFrame, 'y')) {
    topMeasurement = new Measurement({
      startRect: selectedFrame,
      endRect: hoveredFrame,
      edge: 'minY',
    })

    bottomMeasurement = new Measurement({
      startRect: selectedFrame,
      endRect: hoveredFrame,
      edge: 'maxY',
    })
  } else {
    if (rectMaxY(selectedFrame) > rectMaxY(hoveredFrame)) {
      topMeasurement = new Measurement({
        startRect: selectedFrame,
        endRect: hoveredFrame,
        edge: 'minY',
      })
    }

    if (rectMinY(selectedFrame) < rectMinY(hoveredFrame)) {
      bottomMeasurement = new Measurement({
        startRect: selectedFrame,
        endRect: hoveredFrame,
        edge: 'maxY',
      })
    }
  }

  return (
    <Container {...rest}>
      {!!topMeasurement?.length && (
        <MeasurementView
          measurement={topMeasurement}
          side="top"
          zoomFactor={zoomFactor}
        />
      )}
      {!!rightMeasurement?.length && (
        <MeasurementView
          measurement={rightMeasurement}
          side="right"
          zoomFactor={zoomFactor}
        />
      )}
      {!!bottomMeasurement?.length && (
        <MeasurementView
          measurement={bottomMeasurement}
          side="bottom"
          zoomFactor={zoomFactor}
        />
      )}
      {!!leftMeasurement?.length && (
        <MeasurementView
          measurement={leftMeasurement}
          side="left"
          zoomFactor={zoomFactor}
        />
      )}
    </Container>
  )
}

export default Measurements
